// components/BarChartSection.tsx

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

// Standard Office/Excel categorical palette, one color per bar (matches the reference chart).
const COLORS = ["#4472C4", "#ED7D31", "#A5A5A5", "#FFC000", "#70AD47"];

const mapChartData = (data = []) =>
  data.map((item, index) => ({
    section: item.section,
    value: item.average,
    color: COLORS[index % COLORS.length],
  }));

// Recharts SVG <text> doesn't wrap on its own, so long labels like
// "GRADE 10 — COMBINED (ALL CAMPUSES)" need to be split into lines manually.
const wrapLabel = (label = "", maxCharsPerLine = 12) => {
  const words = label.split(" ");
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxCharsPerLine && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });

  if (current) lines.push(current);
  return lines;
};

const WrappedTick = ({ x, y, payload }) => {
  const lines = wrapLabel(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, index) => (
        <text
          key={line}
          x={0}
          y={0}
          dy={12 + index * 13}
          textAnchor="middle"
          fill="#94A3B8"
          fontSize={11}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const yAxisMax = (dataMax) => Math.ceil((dataMax + 10) / 20) * 20;

const BarChartSection = ({ data = [] }) => {
  const chartData = mapChartData(data);

  return (
    <div className="rounded-3xl bg-white p-6 shadow-sm">
      {/* Heading */}
      <h2 className="mb-6 text-xl font-semibold text-[#1f2a5a]">
        Average Overall % by Section/Campus
      </h2>

      {/* Chart */}
      <div className="h-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 10,
              right: 10,
              left: -10,
              bottom: 10,
            }}
          >
            {/* Grid */}
            <CartesianGrid
              strokeDasharray="0"
              vertical={false}
              stroke="#EEF2F7"
            />

            {/* X Axis */}
            <XAxis
              dataKey="section"
              tickLine={false}
              axisLine={false}
              interval={0}
              height={55}
              tick={<WrappedTick />}
            />

            {/* Y Axis */}
            <YAxis
              domain={[0, yAxisMax]}
              tickCount={5}
              tickLine={false}
              axisLine={false}
              tick={{
                fill: "#94A3B8",
                fontSize: 12,
              }}
            />

            {/* Tooltip */}
            <Tooltip
              cursor={{ fill: "transparent" }}
              contentStyle={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            />

            {/* Legend */}
            <Legend
              verticalAlign="top"
              align="right"
              iconType="square"
              formatter={() => "Average % (student-weighted)"}
              wrapperStyle={{ fontSize: "13px", color: "#475569" }}
            />

            {/* Bars */}
            <Bar
              name="Average % (student-weighted)"
              dataKey="value"
              fill={COLORS[0]}
              radius={[2, 2, 0, 0]}
              barSize={48}
              isAnimationActive={false}
            >
              {chartData.map((entry) => (
                <Cell key={entry.section} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChartSection;
